import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import rawgApi from '../../data/api/rawg/rawg-api'
import gameCommentApi, {
  GameComment
} from '../../data/api/user/game-comment-api'
import { getLoggedInUser } from '../../data/auth/auth-storage'

const GameDetailsPage = (): React.JSX.Element => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [game, setGame] = useState<any>(null)
  const [selectedScreenshot, setSelectedScreenshot] = useState<string | null>(
    null
  )
  const [screenshots, setScreenshots] = useState<any[]>([])
  const [movies, setMovies] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const [comments, setComments] = useState<GameComment[]>([])
  const [commentText, setCommentText] = useState('')
  const [commentsLoading, setCommentsLoading] = useState(true)
  const [commentSubmitting, setCommentSubmitting] = useState(false)
  const [commentError, setCommentError] = useState('')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const loadGame = async () => {
      try {
        const detailsResponse = await rawgApi.getGameDetails(Number(id))

        const gameData =
          typeof detailsResponse.data === 'string'
            ? JSON.parse(detailsResponse.data)
            : detailsResponse.data

        setGame(gameData)

        try {
          const screenshotsResponse =
            await rawgApi.getGameScreenshots(Number(id))

          const screenshotsData =
            typeof screenshotsResponse.data === 'string'
              ? JSON.parse(screenshotsResponse.data)
              : screenshotsResponse.data

          setScreenshots(screenshotsData.results || [])
        } catch (err) {
          console.error('Failed to load screenshots:', err)
          setScreenshots([])
        }

        try {
          const moviesResponse = await rawgApi.getGameMovies(Number(id))

          const moviesData =
            typeof moviesResponse.data === 'string'
              ? JSON.parse(moviesResponse.data)
              : moviesResponse.data

          setMovies(moviesData.results || [])
        } catch (err) {
          console.error('Failed to load movies:', err)
          setMovies([])
        }
      } catch (err) {
        console.error('Failed to load game details:', err)
      } finally {
        setLoading(false)
      }
    }

    loadGame()
  }, [id])

  useEffect(() => {
    const loadComments = async () => {
      if (!id) {
        setComments([])
        setCommentsLoading(false)
        return
      }

      try {
        setCommentsLoading(true)
        setCommentError('')

        const loadedComments =
          await gameCommentApi.getGameComments(id)

        setComments(loadedComments)
      } catch (err) {
        console.error('Failed to load comments:', err)
        setComments([])
        setCommentError('Failed to load comments.')
      } finally {
        setCommentsLoading(false)
      }
    }

    loadComments()
  }, [id])

  const handleAddComment = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()

    const loggedInUser = getLoggedInUser() as any

    if (!loggedInUser || !id) {
      setCommentError(
        'Please log in before leaving a comment.'
      )
      return
    }

    const userId = Number(
      loggedInUser.id || localStorage.getItem('userId')
    )

    if (!userId) {
      setCommentError(
        'Please log in before leaving a comment.'
      )
      return
    }

    if (!commentText.trim()) {
      setCommentError(
        'Please enter a comment before submitting.'
      )
      return
    }

    const fullName = [
      loggedInUser.firstName,
      loggedInUser.lastName
    ]
      .filter(Boolean)
      .join(' ')
      .trim()

    const userName =
      fullName ||
      loggedInUser.name ||
      loggedInUser.email ||
      'User'

    try {
      setCommentSubmitting(true)
      setCommentError('')

      const newComment =
        await gameCommentApi.addComment({
          gameId: id,
          userId,
          userName,
          commentText: commentText.trim()
        })

      setComments((currentComments) => [
        newComment,
        ...currentComments
      ])

      setCommentText('')
    } catch (err) {
      console.error('Failed to add comment:', err)
      setCommentError('Failed to add comment.')
    } finally {
      setCommentSubmitting(false)
    }
  }

const handleDeleteComment = async (commentId: number) => {
  const loggedInUser = getLoggedInUser() as any

  if (!loggedInUser) {
    setCommentError('Please log in before deleting a comment.')
    return
  }

  const userId = Number(
    loggedInUser.id || localStorage.getItem('userId')
  )

  try {
    await gameCommentApi.deleteComment(commentId, userId)

    setComments((currentComments) =>
      currentComments.filter(
        (comment) => comment.id !== commentId
      )
    )
  } catch (err) {
    console.error('Failed to delete comment:', err)
    setCommentError('Failed to delete comment.')
  }
}


  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background:
            'linear-gradient(180deg, #0b1220 0%, #0f1724 100%)'
        }}
      >
        <CircularProgress sx={{ color: '#f8fbff' }} />
      </Box>
    )
  }

  if (!game) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background:
            'linear-gradient(180deg, #0b1220 0%, #0f1724 100%)'
        }}
      >
        <Typography sx={{ color: '#f8fbff' }}>
          Game not found.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        marginLeft: 'calc(50% - 50vw)',
        p: 4,
        background:
          'linear-gradient(180deg, #0b1220 0%, #0f1724 100%)',
        color: '#f8fbff'
      }}
    >
      <Paper
        sx={{
          maxWidth: 1000,
          mx: 'auto',
          p: 4,
          borderRadius: 3,
          background:
            'linear-gradient(120deg, rgba(59,130,246,0.24), rgba(2,6,23,0.65))',
          border: '1px solid rgba(148,163,184,0.35)',
          color: '#f8fbff',
          boxShadow: 'none',
          '& .MuiTypography-root': {
            color: '#f8fbff'
          }
        }}
      >
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          sx={{
            mb: 3,
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#2563eb'
            }
          }}
        >
          ← Back
        </Button>

        {game.background_image && (
          <Box
            component="img"
            src={game.background_image}
            alt={game.name}
            sx={{
              width: '100%',
              maxHeight: 520,
              objectFit: 'cover',
              borderRadius: 2,
              mb: 3,
              boxShadow: '0 12px 30px rgba(0,0,0,0.35)'
            }}
          />
        )}

        <Typography variant="h3" gutterBottom>
          {game.name}
        </Typography>

        <Typography sx={{ color: '#c2cedd !important' }}>
            <strong>Released:</strong>{' '}
            {game.released
              ? new Date(game.released).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric',})
              : 'Unknown'}
          </Typography>

        <Typography sx={{ color: '#c2cedd !important' }}>
          <strong>Rating:</strong> {game.rating || 'N/A'}
        </Typography>

        <Typography sx={{ color: '#c2cedd !important' }}>
          <strong>Metacritic:</strong> {game.metacritic || 'N/A'}
        </Typography>

        <Typography sx={{ color: '#c2cedd !important' }}>
          <strong>Genres:</strong>{' '}
          {game.genres?.length
            ? game.genres.map((genre: any) => genre.name).join(', ')
            : 'Unknown'}
        </Typography>

        <Typography sx={{ color: '#c2cedd !important' }}>
          <strong>Developers:</strong>{' '}
          {game.developers?.length
            ? game.developers
                .map((developer: any) => developer.name)
                .join(', ')
            : 'Unknown'}
        </Typography>

        <Typography sx={{ color: '#c2cedd !important' }}>
          <strong>Publishers:</strong>{' '}
          {game.publishers?.length
            ? game.publishers
                .map((publisher: any) => publisher.name)
                .join(', ')
            : 'Unknown'}
        </Typography>

        <Typography
          sx={{
            mt: 3,
            lineHeight: 1.7,
            color: '#c2cedd !important'
          }}
        >
          {game.description_raw || 'No description available.'}
        </Typography>

        <Box sx={{ mt: 5 }}>
          <Typography variant="h5" gutterBottom>
            Screenshots
          </Typography>

          {screenshots.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 2
              }}
            >
              {screenshots.map((shot: any) => (
                <Box
                  key={shot.id}
                  component="img"
                  src={shot.image}
                  alt={`${game.name} screenshot`}
                  onClick={() => setSelectedScreenshot(shot.image)}
                  sx={{
                    width: '100%',
                    height: 180,
                    objectFit: 'cover',
                    borderRadius: 2,
                    boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                    cursor: 'pointer',
                    transition: '0.2s',
                    '&:hover': {
                      transform: 'scale(1.03)'
                    }
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography
              sx={{
                color: '#c2cedd !important'
              }}
            >
              No screenshots available.
            </Typography>
          )}
        </Box>

        {movies.length > 0 && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h5" gutterBottom>
              Gameplay Trailer
            </Typography>

            <video
              controls
              width="100%"
              poster={movies[0].preview}
              style={{
                borderRadius: '8px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)'
              }}
            >
              <source
                src={movies[0].data?.max}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </Box>
        )}

        <Paper
          sx={{
            mt: 5,
            p: 3,
            borderRadius: 3,
            backgroundColor: 'rgba(15, 23, 36, 0.75)',
            border: '1px solid rgba(148,163,184,0.35)',
            color: '#f8fbff',
            boxShadow: 'none'
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
          >
            Comments
          </Typography>

          <Box
            component="form"
            onSubmit={handleAddComment}
            sx={{
              mb: 4
            }}
          >
            <TextField
              placeholder="Leave a comment"
              multiline
              minRows={3}
              fullWidth
              value={commentText}
              onChange={(event) =>
                setCommentText(event.target.value)
              }
              disabled={commentSubmitting}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  borderRadius: 2
                }
              }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={
                commentSubmitting ||
                !commentText.trim()
              }
              sx={{
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#2563eb'
                }
              }}
            >
              {commentSubmitting
                ? 'Posting...'
                : 'Post Comment'}
            </Button>
          </Box>

          {commentError && (
            <Alert
              severity="error"
              sx={{
                mb: 3
              }}
            >
              {commentError}
            </Alert>
          )}

          {commentsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 4
              }}
            >
              <CircularProgress sx={{ color: '#f8fbff' }} />
            </Box>
          ) : comments.length === 0 ? (
            <Typography
              sx={{
                color: '#c2cedd !important'
              }}
            >
              No comments yet. Be the first to comment.
            </Typography>
          ) : (
            <Box>
              {comments.map((comment, index) => (
                <Box
                  key={comment.id || index}
                  sx={{
                    py: 2
                  }}
                >
                  <Typography fontWeight="bold">
                    {comment.userName}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        'rgba(255,255,255,0.65) !important'
                    }}
                  >
                    {comment.createdAt
                      ? new Date(
                          comment.createdAt
                        ).toLocaleString()
                      : ''}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1,
                      color: '#c2cedd !important',
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere'
                    }}
                  >
                    {comment.commentText}
                  </Typography>

                  {comment.userId ===
                    Number(localStorage.getItem('userId')) && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() =>
                        handleDeleteComment(comment.id!)
                      }
                    >
                      Delete
                    </Button>
                  )}

                  {index < comments.length - 1 && (
                    <Divider
                      sx={{
                        mt: 2,
                        borderColor:
                          'rgba(255,255,255,0.15)'
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Dialog
          open={selectedScreenshot !== null}
          onClose={() => setSelectedScreenshot(null)}
          maxWidth="lg"
          PaperProps={{
            sx: {
              backgroundColor: '#0f1724',
              border: '1px solid rgba(148,163,184,0.35)'
            }
          }}
        >
          {selectedScreenshot && (
            <Box
              component="img"
              src={selectedScreenshot}
              alt={`${game.name} enlarged screenshot`}
              sx={{
                width: '100%',
                maxWidth: '1000px',
                display: 'block'
              }}
            />
          )}
        </Dialog>
      </Paper>
    </Box>
  )
}

export default GameDetailsPage